import { MenuItem, MenuList, Paper, Popper, Typography } from "@mui/material";
import * as React from "react";

import { MirrorHighlightTextarea } from "@/shared/components";
import { TOPIC_SEGMENT_CLASS } from "@/shared/components/topicReferenceTokens";
import {
  activeTopicTokenAtCursor,
  segmentTopicReferences,
  splitTopicQuery,
  TOPIC_TOKEN_CHAR,
} from "@/shared/domain/skills/topicReference";
import { useTopicFields } from "@/shared/domain/topics/hooks";
import { useCaretAutocomplete, useComboboxIds } from "@/shared/hooks";

import styles from "./TopicBodyEditor.module.css";

/**
 * Suggestions shown at once in the autocomplete menu — one page. The menu shows
 * one page at a time (no scrolling) and ArrowUp/Down page through all matches;
 * there is no cap, since only the on-screen page is ever built into menu items.
 */
const PAGE_SIZE = 5;

interface Props {
  /**
   * Rendered as the editor's own label. Omit it when the editor sits inside a
   * block whose heading already names the field, and pass {@link labelledBy}
   * instead — exactly one of the two is required, or the textarea ends up with
   * no accessible name.
   */
  label?: string;
  /**
   * Id of an existing element naming this field (a
   * {@link ../SkillFieldSection} heading). Suppresses the internal label.
   */
  labelledBy?: string;
  value: string;
  onChange: (next: string) => void;
  /** Org topic names offered after a `[[` and highlighted when referenced. */
  topicNames: readonly string[];
  /** Owning org, used to fetch a referenced topic's fields for `/topic.` drill-in. */
  orgId: string | undefined;
  helperText?: string;
  /** Renders `helperText` as a validation message rather than a hint. */
  error?: boolean;
  /** Fired when focus leaves the textarea — form libraries use it to mark the
   * field touched. Composes with the editor's own focus tracking. */
  onBlur?: () => void;
  minRows?: number;
  required?: boolean;
  /** When true, the editor flexes to fill its parent's height (rather than a
   * fixed `minRows` height) — used to give the body most of a tall dialog. */
  grow?: boolean;
  /** Extra className applied to both text layers (e.g. the monospace body font). */
  inputClassName?: string;
}

interface Suggestion {
  /** Menu label. */
  label: string;
  /** Full bracketed token (`[[…]]`) placed from the token's start on accept. */
  insertText: string;
  /** Secondary label shown muted to the right (e.g. "topic" / "field"). */
  kind: string;
  /**
   * Caret offset within ``insertText`` after accepting — set on topic
   * suggestions to park the caret before the autoclosed `]]` so typing `.`
   * drills straight into the topic's fields. Absent on field suggestions,
   * which are terminal (caret lands after the reference).
   */
  caretOffset?: number;
}

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * Multiline skill-body editor with a `[[` autocomplete over the org's topics
 * and live highlighting of `[[topic.field]]` references.
 *
 * The visible surface is a mirror ``<div>`` rendered behind a transparent-text
 * ``<textarea>``: the caret and editing come from the textarea, the coloring
 * from the overlay, whose fonts and padding match exactly so the two stay in
 * lockstep. References (via {@link segmentTopicReferences}) are wrapped in
 * highlight chips — the same references the skill persists as
 * ``relevant_topics`` and the chat AI pre-loads schemas for.
 *
 * Typing `[[` opens a menu of topic names; accepting one autocloses the
 * brackets and parks the caret inside them, so typing `.` switches the menu
 * to that topic's own fields (fetched by name from the shared-topics/fields
 * schema). Typing any character that can't continue the token (space, Enter,
 * punctuation) hops the caret out past the `]]` first, so prose flows on
 * naturally; `]` overtypes the autoclosed pair. Only the topic name is
 * persisted; the field suffix is authoring prose.
 */
export function TopicBodyEditor({
  label,
  labelledBy,
  value,
  onChange,
  topicNames,
  orgId,
  helperText,
  error,
  onBlur,
  minRows = 6,
  required,
  grow,
  inputClassName,
}: Props) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = React.useState(false);

  // Shared caret-autocomplete engine (the `/`-menu state machine, overlay
  // scroll sync, keyboard nav, and token-replacing accept). This component
  // supplies the topic-token detector and its own suggestion + overlay
  // rendering below.
  const {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    anchorEl,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  } = useCaretAutocomplete({
    onChange,
    textareaRef,
    overlayRef,
    paperRef,
    detectToken: activeTopicTokenAtCursor,
    // Topic names can be long slash paths; keep the menu under the caret the
    // author is typing at rather than pinned to the far-left `[[`.
    anchorTo: "caret",
  });

  const ownLabelId = React.useId();
  // An external heading naming the field wins; otherwise the editor labels
  // itself. Either way the textarea gets exactly one accessible name.
  const labelId = labelledBy ?? ownLabelId;
  const { listboxId, optionId } = useComboboxIds();

  // Split the body into plain / highlighted segments for the overlay
  // (segmentTopicReferences is shared with the markdown preview so both
  // surfaces color a reference identically). Rebuilt only when the text or
  // the topic catalog changes.
  const segments = React.useMemo(
    () => segmentTopicReferences(value, topicNames),
    [value, topicNames],
  );

  // The complete, known topic the caret is currently drilling into
  // (`[[foo.` → `foo`), or null when not in field mode or the topic isn't a
  // known one. Drives the lazy per-topic field fetch below.
  const activeTopicName = React.useMemo(() => {
    if (autocomplete === null) {
      return null;
    }
    const { topicPart, fieldFragment } = splitTopicQuery(
      autocomplete.query,
      topicNames,
    );
    if (fieldFragment === null) {
      return null;
    }
    return topicNames.includes(topicPart) ? topicPart : null;
  }, [autocomplete, topicNames]);

  const topicFieldsQuery = useTopicFields(activeTopicName, orgId);
  // Stabilize the reference so the suggestions memo below doesn't recompute on
  // every render (a bare `?? []` allocates a fresh array each time).
  const topicFields = React.useMemo(
    () => topicFieldsQuery.data ?? [],
    [topicFieldsQuery.data],
  );

  // The full filtered match list as plain names, plus a builder that turns one
  // match into its menu Suggestion. Matches are kept uncapped so pagination can
  // page through every match; only the ~5 on screen (and the accepted one) are
  // ever materialized into Suggestion objects, so per-keystroke work stays flat
  // regardless of catalog size.
  const { matches, makeSuggestion } = React.useMemo(() => {
    if (autocomplete === null) {
      return { matches: [] as string[], makeSuggestion: null };
    }
    const { topicPart, fieldFragment } = splitTopicQuery(
      autocomplete.query,
      topicNames,
    );
    if (fieldFragment === null) {
      const q = topicPart.toLowerCase();
      // Topic accept autocloses the brackets and parks the caret before the
      // `]]` (caretOffset), so the author can drill into fields with `.` or
      // hop out by just continuing to type prose.
      return {
        matches: topicNames.filter((name) => name.toLowerCase().includes(q)),
        makeSuggestion: (name: string): Suggestion => ({
          label: name,
          insertText: `[[${name}]]`,
          kind: "topic",
          caretOffset: 2 + name.length,
        }),
      };
    }
    const q = fieldFragment.toLowerCase();
    // Field mode: suggest the active topic's own fields (fetched by name).
    // Field accept is terminal — the reference is complete, the caret lands
    // after the `]]`.
    return {
      matches: topicFields.filter((path) => path.toLowerCase().includes(q)),
      makeSuggestion: (path: string): Suggestion => ({
        label: path,
        insertText: `[[${topicPart}.${path}]]`,
        kind: "field",
      }),
    };
  }, [autocomplete, topicNames, topicFields]);

  // Keep the highlight valid as the filtered list shrinks — the topic-fields
  // fetch resolves async, so the list can grow or shrink after the menu opens.
  React.useEffect(() => {
    clampHighlight(matches.length);
  }, [clampHighlight, matches.length]);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    refresh(
      event.target.value,
      event.target.selectionEnd ?? event.target.value.length,
    );
  }

  function handleSelect() {
    const ta = textareaRef.current;
    if (ta === null) {
      return;
    }
    refresh(ta.value, ta.selectionEnd ?? ta.value.length);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const consumed = handleNavKeyDown(event, matches.length, (index) => {
      const suggestion = makeSuggestion?.(matches[index]);
      if (suggestion !== undefined) {
        accept(suggestion.insertText, suggestion.caretOffset);
      }
    });
    if (!consumed) {
      handleBracketHopOut(event);
    }
  }

  /**
   * The brackets behave like a token container you step out of, not into: with
   * the caret parked immediately before an autoclosed `]]` (inside an active
   * token), several keys jump the caret past the whole `]]` pair rather than
   * landing between the two brackets —
   *   - Right arrow moves out cleanly (no text change).
   *   - A typed character that cannot continue the token (space, Enter,
   *     punctuation) hops out first, so the browser's default insertion lands
   *     outside the brackets and prose flows on naturally.
   *   - `]` overtypes the existing pair instead of inserting a third bracket.
   * Token characters (including the `.` field trigger) fall through to normal
   * editing inside the brackets. Moving the caret synchronously in keydown
   * (rather than splicing the value ourselves) keeps native undo history intact.
   */
  function handleBracketHopOut(
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) {
    const ta = textareaRef.current;
    if (ta === null || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const caret = ta.selectionStart ?? 0;
    if (ta.selectionEnd !== caret) {
      return;
    }
    const token = activeTopicTokenAtCursor(value, caret);
    // Only act while the caret sits immediately before an autoclosed `]]`
    // (tokenEnd past the caret); otherwise let editing proceed normally.
    if (token === null || token.tokenEnd === caret) {
      return;
    }
    const hopped = token.tokenEnd;

    // Right arrow steps over the whole pair instead of into its middle.
    if (event.key === "ArrowRight" && !event.shiftKey) {
      event.preventDefault();
      ta.setSelectionRange(hopped, hopped);
      refresh(value, hopped);
      return;
    }

    const isOvertype = event.key === "]";
    // Enter, or any single character that can't continue the token, hops the
    // caret out past the `]]`; token characters fall through to normal editing.
    const isHopOut =
      event.key === "Enter" ||
      (event.key.length === 1 && !TOPIC_TOKEN_CHAR.test(event.key));
    if (!isOvertype && !isHopOut) {
      return;
    }
    if (isOvertype) {
      // The pair already exists; consume the keystroke and step past it.
      event.preventDefault();
      refresh(value, hopped);
    }
    ta.setSelectionRange(hopped, hopped);
  }

  const menuOpen = autocomplete !== null && matches.length > 0;
  const activeOptionId =
    menuOpen && autocomplete !== null
      ? optionId(autocomplete.highlight)
      : undefined;

  // Show one page of suggestions at a time (no scroll): the page is whichever
  // PAGE_SIZE-sized window contains the highlight, so ArrowUp/Down moving the
  // highlight past a page edge flips to the next/previous page. Options keep
  // their absolute index (for ids, selection, and accept).
  const pageStart = autocomplete
    ? Math.floor(autocomplete.highlight / PAGE_SIZE) * PAGE_SIZE
    : 0;
  const pageMatches = matches.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className={cx(styles.root, grow && styles.rootGrow)}>
      {label !== undefined && (
        <label id={ownLabelId} className={styles.label}>
          {`${label}${required ? " *" : ""}`}
        </label>
      )}
      <div
        className={cx(
          styles.field,
          focused && styles.fieldFocused,
          grow && styles.fieldGrow,
        )}
      >
        <MirrorHighlightTextarea
          value={value}
          overlay={segments.map((segment, idx) => (
            <span key={idx} className={TOPIC_SEGMENT_CLASS[segment.kind]}>
              {segment.text}
            </span>
          ))}
          textareaRef={textareaRef}
          overlayRef={overlayRef}
          onChange={handleChange}
          onSelect={handleSelect}
          onScroll={syncOverlayScroll}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          expanded={menuOpen}
          listboxId={listboxId}
          activeOptionId={activeOptionId}
          labelId={labelId}
          required={required}
          rows={minRows}
          textClassName={cx(styles.bodyText, inputClassName)}
          textareaClassName={grow ? styles.textareaGrow : styles.textareaResize}
        />
      </div>
      {helperText !== undefined && (
        <p className={cx(styles.helperText, error && styles.helperTextError)}>
          {helperText}
        </p>
      )}
      <Popper
        open={menuOpen && anchorEl !== null}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        className={styles.popper}
      >
        <Paper ref={paperRef} elevation={4} className={styles.paper}>
          <MenuList dense id={listboxId} role="listbox">
            {pageMatches.map((match, i) => {
              const idx = pageStart + i;
              const suggestion = makeSuggestion?.(match);
              if (suggestion === undefined || suggestion === null) {
                return null;
              }
              return (
                <MenuItem
                  key={suggestion.insertText}
                  id={optionId(idx)}
                  role="option"
                  aria-selected={idx === autocomplete?.highlight}
                  selected={idx === autocomplete?.highlight}
                  onClick={() =>
                    accept(suggestion.insertText, suggestion.caretOffset)
                  }
                  onMouseEnter={() => setHighlight(idx)}
                >
                  <span className={styles.suggestionLabel}>
                    {suggestion.label}
                  </span>
                  <span className={styles.suggestionKind}>
                    {suggestion.kind}
                  </span>
                </MenuItem>
              );
            })}
          </MenuList>
          <div className={styles.footer}>
            <Typography variant="caption" className={styles.hint}>
              ↑↓ to navigate · Enter or Tab to insert · Esc to dismiss
            </Typography>
          </div>
        </Paper>
      </Popper>
    </div>
  );
}
